import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageType } from '@frontend/package/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUserType } from '@frontend/transaction/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { hasNavigationEntityContext, hasWizardEntityContext, resolveCurrentPackage } from '../../utils/resolve-wizard-package';
import { useScreen } from '../useScreen';

export type ProveIdentityPhase = 'loading' | 'authenticating' | 'denied';

function resolveProofTransactionUserType(packageType: PackageType | null | undefined): TransactionUserType | null {
    switch (packageType) {
        case PackageType.DROP_OFF:
            return TransactionUserType.SENDER;
        case PackageType.PICK_UP:
            return TransactionUserType.RECEIVER;
        default:
            return null;
    }
}

function pickPackageForProof(packages: Package[]): Package | undefined {
    return packages.find((pkg) => pkg.require_proof_of_identity) ?? packages[0];
}

interface ProveIdentityVerification {
    phase: ProveIdentityPhase;
    isProcessing: boolean;
    accessDenied: boolean;
    error: string | null;
    alreadyAuthenticated: boolean;
    accessDeniedMessage: string;
    resetError: () => void;
    verifyUserAccess: (userId: string) => Promise<void>;
    setError: (error: string | null) => void;
    setIsProcessing: (value: boolean) => void;
    identificationErrorMessage: string;
    authenticationFailedMessage: string;
}

interface UseProveIdentityVerificationOptions {
    /** When true, unauthenticated users must sign in before the screen can continue. */
    requireAuthentication?: boolean;
}

/**
 * Shared gate + transaction-user verification for prove-identity screens.
 */
export const useProveIdentityVerification = (options: UseProveIdentityVerificationOptions = {}): ProveIdentityVerification => {
    const { requireAuthentication = false } = options;
    const intl = useIntl();
    const { store, navigationState, onSubmit } = useScreen();
    const { state: locationState } = useLocation();

    const [phase, setPhase] = useState<ProveIdentityPhase>('loading');
    const [isProcessing, setIsProcessing] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isResolvingPackage, setIsResolvingPackage] = useState(false);
    /** Bumped by resetError after a package-resolve failure so the effect re-runs. */
    const [packageResolveRetryKey, setPackageResolveRetryKey] = useState(0);

    const skipExecutedRef = useRef(false);
    const verificationExecutedRef = useRef(false);
    const packageResolveAttemptedRef = useRef(false);
    const packageResolveInFlightRef = useRef(false);
    const packageResolveFailedRef = useRef(false);
    const requiresProofRef = useRef(false);
    const transactionUserTypeRef = useRef<TransactionUserType | null>(null);
    const accessDeniedRef = useRef(false);

    const transactionContext = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: false,
        autoFetch: true
    });

    const packageContext = useWizardPackage({
        wizardId: store?.wizard.id,
        store,
        autoCreate: false,
        autoFetch: true
    });

    const identificationErrorMessage = intl.formatMessage({
        id: 'kiosk.wizard.prove-identity.identification-error',
        description: 'Error shown when no matching identification is found on the prove identity screen',
        defaultMessage: 'No matching identification was found. Please try again.'
    });

    const accessDeniedMessage = intl.formatMessage({
        id: 'kiosk.wizard.prove-identity.access-denied',
        description: 'Error shown when the authenticated user is not allowed to handle the transaction on the prove identity screen',
        defaultMessage: 'You are not allowed to handle this transaction.'
    });

    const authenticationFailedMessage = intl.formatMessage({
        id: 'kiosk.wizard.prove-identity.authentication-failed',
        description: 'Error shown when user authentication fails on the prove identity screen',
        defaultMessage: 'We could not authenticate you. Please try again.'
    });

    const verificationFailedMessage = intl.formatMessage({
        id: 'kiosk.wizard.prove-identity.verification-failed',
        description: 'Error shown when verifying transaction user access fails on the prove identity screen',
        defaultMessage: 'Something went wrong. Please try again.'
    });

    const verifyUserAccess = useCallback(
        async (userId: string) => {
            if (verificationExecutedRef.current || accessDeniedRef.current) return;

            const requiresProof = requiresProofRef.current;
            const transactionUserType = transactionUserTypeRef.current;
            const transactionId = transactionContext.transaction?.id ?? store?.data.transaction?.id;

            if (!requiresProof || !transactionId || !transactionUserType) {
                skipExecutedRef.current = true;
                onSubmit();
                return;
            }

            verificationExecutedRef.current = true;
            setIsProcessing(true);
            setError(null);

            try {
                const transactionUsers = await TransactionUserClient.fetchTransactionUsers({
                    transaction_id: transactionId,
                    type: transactionUserType
                });

                const isAllowed = transactionUsers.results.some((transactionUser) => transactionUser.user_id === userId);
                if (isAllowed) {
                    onSubmit();
                    return;
                }

                accessDeniedRef.current = true;
                setAccessDenied(true);
                setError(accessDeniedMessage);
                setPhase('denied');
            } catch (e) {
                Logger.error('Failed to verify transaction user access on prove identity screen', e);
                // Keep the guard so the effect cannot auto-retry; resetError allows a manual retry.
                setError(verificationFailedMessage);
            } finally {
                setIsProcessing(false);
            }
        },
        [accessDeniedMessage, onSubmit, store?.data.transaction?.id, transactionContext.transaction?.id, verificationFailedMessage]
    );

    // Resolve package by transaction when PIN/QR only seeded the transaction.
    useEffect(() => {
        const transactionId = transactionContext.transaction?.id ?? store?.data.transaction?.id;
        const existingPackage =
            resolveCurrentPackage(store) ??
            (packageContext.remotePackage as Partial<Package> | null) ??
            (locationState as { package?: Partial<Package> } | null)?.package ??
            (packageContext.package as Partial<Package> | null);

        if (!transactionId || existingPackage || packageResolveInFlightRef.current || packageResolveAttemptedRef.current || !store) {
            return;
        }

        packageResolveInFlightRef.current = true;
        setIsResolvingPackage(true);

        PackageClient.fetchPackages({ transaction_id: transactionId })
            .then(async (response) => {
                const pkg = pickPackageForProof(response.results);
                if (pkg) {
                    await packageContext.localUpdatePackage(pkg);
                }
            })
            .catch((e) => {
                Logger.error('Failed to resolve package for prove identity screen', e);
                packageResolveFailedRef.current = true;
                setError(verificationFailedMessage);
            })
            .finally(() => {
                packageResolveInFlightRef.current = false;
                packageResolveAttemptedRef.current = true;
                setIsResolvingPackage(false);
            });
    }, [
        store,
        locationState,
        transactionContext.transaction?.id,
        packageContext.package,
        packageContext.remotePackage,
        packageContext.localUpdatePackage,
        verificationFailedMessage,
        packageResolveRetryKey
    ]);

    useEffect(() => {
        if (
            skipExecutedRef.current ||
            accessDeniedRef.current ||
            packageResolveFailedRef.current ||
            !store ||
            transactionContext.isLoading ||
            packageContext.isLoading ||
            isResolvingPackage ||
            packageResolveInFlightRef.current
        ) {
            return;
        }

        const hasStoreEntityContext = hasWizardEntityContext(store);
        const hasPendingNavigationContext = hasNavigationEntityContext(locationState) && !hasStoreEntityContext;

        if (hasPendingNavigationContext) {
            return;
        }

        const transactionId = transactionContext.transaction?.id ?? store.data.transaction?.id;
        const currentPackage =
            resolveCurrentPackage(store) ??
            (packageContext.remotePackage as Partial<Package> | null) ??
            (locationState as { package?: Partial<Package> } | null)?.package ??
            (packageContext.package as Partial<Package> | null);

        // Wait until package resolve finishes when we only have a transaction.
        if (transactionId && !currentPackage && !packageResolveAttemptedRef.current) {
            return;
        }

        const requiresProof = currentPackage?.require_proof_of_identity ?? false;
        const transactionUserType = resolveProofTransactionUserType(currentPackage?.type);

        requiresProofRef.current = requiresProof;
        transactionUserTypeRef.current = transactionUserType;

        if (requireAuthentication) {
            if (navigationState.user?.id) {
                setPhase('authenticating');
                void verifyUserAccess(navigationState.user.id);
                return;
            }

            setPhase('authenticating');
            return;
        }

        if (!hasStoreEntityContext && !transactionContext.remoteTransaction && !packageContext.remotePackage) {
            skipExecutedRef.current = true;
            onSubmit();
            return;
        }

        if (!requiresProof) {
            skipExecutedRef.current = true;
            onSubmit();
            return;
        }

        if (!transactionUserType) {
            skipExecutedRef.current = true;
            onSubmit();
            return;
        }

        if (navigationState.user?.id) {
            setPhase('authenticating');
            void verifyUserAccess(navigationState.user.id);
            return;
        }

        setPhase('authenticating');
    }, [
        store,
        locationState,
        requireAuthentication,
        isResolvingPackage,
        transactionContext.isLoading,
        transactionContext.remoteTransaction,
        transactionContext.transaction?.id,
        packageContext.isLoading,
        packageContext.package,
        packageContext.remotePackage,
        navigationState.user?.id,
        onSubmit,
        verifyUserAccess
    ]);

    const resetError = useCallback(() => {
        setError(null);

        // Transient package-resolve failure: allow another attempt.
        if (packageResolveFailedRef.current) {
            packageResolveFailedRef.current = false;
            packageResolveAttemptedRef.current = false;
            packageResolveInFlightRef.current = false;
            setPackageResolveRetryKey((key) => key + 1);
            return;
        }

        // Already-authenticated wrong user: stay denied; back is the escape hatch.
        if (accessDeniedRef.current && navigationState.user) {
            return;
        }

        // Badge/itsme flow or transient verification failure: allow trying again.
        if (accessDeniedRef.current || verificationExecutedRef.current) {
            accessDeniedRef.current = false;
            setAccessDenied(false);
            verificationExecutedRef.current = false;
            setPhase('authenticating');
        }
    }, [navigationState.user]);

    return {
        phase,
        isProcessing: isProcessing || isResolvingPackage,
        accessDenied,
        error,
        alreadyAuthenticated: !!navigationState.user,
        accessDeniedMessage,
        resetError,
        verifyUserAccess,
        setError,
        setIsProcessing,
        identificationErrorMessage,
        authenticationFailedMessage
    };
};
